import React, {
  memo,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useContext,
} from 'react';
import { history } from "umi";
import { context as Context } from '@gp/components/Context';
import { Card } from 'tea-component';
import { reportOperationLog } from '@gp/services/global';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import Filter from './components/Filter';
import LegendSign from "@gp/pages/Pipeline/Overview/components/Schedule/components/LegendSign";
import CalendarPanel from '@gp/pages/Pipeline/Overview/components/CalendarPanel';
import useLegendList from '@gp/pages/Pipeline/Overview/components/Schedule/hooks/useLegendList';
import { useRequest } from 'ahooks';
import {
  queryCalendarSelectableTime,
  queryCalendarList,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import { formatDateStr } from '@gp/utils/common';
import { MENU_ID_MAP, SLASH_G_POP, CONSTANT_TEXTS } from '@gp/constants/const';
import { getCommonCalendarList } from '../utils';
import moment from 'moment';
import './index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';

import FullScreenScale from '@home/components/FullScreenScale';
import useScalable from '@gp/pages/Pipeline/Overview/components/Schedule/hooks/useScalable';
import {
  CALENDAR_CARD_SIZES,
  DEFAULT_CALENDAR_CARD_SIZE,
  MIN_CALENDAR_CARD_SIZE,
  MAX_CALENDAR_CARD_SIZE,
} from '@gp/pages/Pipeline/Overview/components/CalendarPanel/constants';

import type { ICalendarPanelInstance } from '@gp/pages/Pipeline/Overview/components/CalendarPanel';

const PARAMS_MAP: {
  [keyName: string]: {
    granularity: 'monthly' | 'quarter' | 'year';
    selectDateFormat: string;
  };
} = {
  month: {
    granularity: 'monthly',
    selectDateFormat: 'YYYY.MM',
  },
  quarter: {
    granularity: 'quarter',
    selectDateFormat: 'YYYY.Q',
  },
  year: {
    granularity: 'year',
    selectDateFormat: 'YYYY',
  },
};
const LENGEND_SIGN_OPT = [
  {
    dotColor: '#A6C9FF',
    text: 'Project Milestone',
  },
  {
    dotColor: '#5EC8F1',
    text: 'Management',
  },
  {
    dotColor: '#B8BED1',
    text: CONSTANT_TEXTS.COMPLETED,
  },
];

interface CalendarDetailsPropsType {}

const DEFAULT_PROPS: CalendarDetailsPropsType = {};

function CalendarDetails(props: CalendarDetailsPropsType) {
  const {} = props;

  const {
    studioId,
    studioName,
    setMeetingId,
    pageAuthorityData,
  }: StudioOverallDetailContextTypes = useContext(Context) || {};

  const [dateType, setDateType] = useState<dateTypes>('quarter');
  const [legendSelected, setLegendSelected] = useState<LegendSelectedTypes>([
    true,
    true,
    true,
  ]);
  // 是否没有Calendar日历的权限
  const [isNoPermission, setIsNoPermission] = useState(false);
  const [dataList, setDataList] = useState<CommonCalendarDataTypes>(null);

  const { loading: filterLoading, data: selectableTime } = useRequest(
    queryCalendarSelectableTime,
    { defaultParams: [{ studio_id: studioId }] },
  );
  const updateTimeFormatted = formatDateStr(selectableTime?.update_time, 'YYYY-MM');
  const { loading, data, run } = useRequest(queryCalendarList, {
    manual: true,
  });

  const filterFn = useCallback((t: CommonCalendarCardItemTypes, legendSelected: boolean[]) => {
    const isProjectMilestone = t?.type === 1;
    const isManagement = t?.type === 0;
    const isExpired = !!t?.isExpire;
    if (isProjectMilestone && legendSelected?.[0]) return true;
    if (isManagement && legendSelected?.[1]) return true;
    if (isExpired && legendSelected?.[2]) return true;
    return false;
  }, []);

  const { legendList } = useLegendList({
    legendSelected,
    loading,
    dataList,
    dateType,
    filterFn,
  });

  // 计算时间可选范围
  const dateRange: string = useMemo(() => {
    if (filterLoading) return '';
    const startTime =
      Number(selectableTime?.ms_start_date || '0') <
        Number(selectableTime?.mgt_start_date || '0') &&
      selectableTime?.ms_start_date
        ? selectableTime?.ms_start_date
        : selectableTime?.mgt_start_date || selectableTime?.ms_start_date || '';
    const endTime =
      Number(selectableTime?.ms_end_date || '0') >
        Number(selectableTime?.mgt_end_date || '0') &&
      selectableTime?.ms_end_date
        ? selectableTime?.ms_end_date
        : selectableTime?.mgt_end_date || selectableTime?.ms_end_date || '';
    if (!startTime || !endTime) return '';

    return `${startTime  }-${  endTime}`;
  }, [filterLoading]);

  // 请求页面数据
  const handleFilterChange = useCallback(
    (category: string[], dateType: dateTypes, date: string[]) => {
      setDateType(dateType);
      const startDateV = moment(
        date[0],
        PARAMS_MAP?.[dateType]?.selectDateFormat,
      );
      const endDateV = moment(
        date[1],
        PARAMS_MAP?.[dateType]?.selectDateFormat,
      );
      const startDate = startDateV.isValid()
        ? startDateV.startOf(dateType).format('YYYY-MM-DD')
        : '';
      const endDate = endDateV.isValid()
        ? endDateV.endOf(dateType).format('YYYY-MM-DD')
        : '';
      run({
        studio_id: studioId,
        category:
          Array.isArray(category) && category?.length === 1
            ? category?.[0]
            : '',
        start_date: startDate,
        end_date: endDate,
        granularity: PARAMS_MAP?.[dateType]?.granularity,
      });
    },
    [],
  );

  const handleLegendChange = useCallback((v: boolean[]) => {
    setLegendSelected([...v] as LegendSelectedTypes);
  }, []);

  const handleCardClick = useCallback((
    cardList: CommonCalendarCardItemTypes | null,
    isJump = true,
  ) => {
    // 跳转产品详情页
    let url = '';
    if (cardList?.type === 1) {
      const filter = encodeURIComponent(JSON.stringify({
        projectId: cardList?.projectId || '',
        project: cardList?.projectName || '',
        menuFrom: MENU_ID_MAP['pipeline_all-projects'], // 用于标识左侧菜单栏当前选中高亮
        tab: 'gameDetail',
      }));
      url = `${SLASH_G_POP  }/Pipeline/ProjectList/Detail?filter=${filter}`;
    }
    // 跳转Management页签
    const meetingId = cardList?.meetingId || '';
    if (cardList?.type === 0) {
      const filter = encodeURIComponent(JSON.stringify({
        studioId,
        studioName,
        menuFrom: MENU_ID_MAP['studio-dashboard_studio-overall'], // 用于标识左侧菜单栏当前选中高亮
        tab: 'management',
        meetingId, // 链接跳转时使用
      }));
      url = `${SLASH_G_POP  }/Studio/PortfolioOverview/Detail?filter=${filter}`;
    }
    if (isJump) {
      cardList?.type === 0 && setMeetingId(String(meetingId));
      history.push(url);
    }

    return url;
  }, [
    studioId,
    studioName,
  ]);

  useEffect(() => {
    if (loading) return;
    const meetingCardAccess = pageAuthorityData?.management && pageAuthorityData?.management_boardGovernance;
    setIsNoPermission(!!data?.no_auth);
    setDataList(getCommonCalendarList(data?.list, true, meetingCardAccess));
  }, [
    loading,
    pageAuthorityData?.management,
    pageAuthorityData?.management_boardGovernance,
  ]);

  // 页面打点
  useEffect(() => {
    reportOperationLog(
      Object?.assign(
        ALL_REPORT_OPERATION_LOG.STUDIO_OVERALL_DETAILS_CALENDAR,
        {
          uidType: 'studio_id',
          uid: studioId,
          extInfo: studioName,
        },
      ),
    );
  }, []);

  const calendarPanel = useRef<ICalendarPanelInstance | null>(null);
  const {
    size: calendarCardSize,
    onSmallerSize: handleCalendarCardSmallerSize,
    onBiggerSize: handleCalendarCardBiggerSize,
    reset: resetCalendarCardSize,
  } = useScalable({
    initialSize: DEFAULT_CALENDAR_CARD_SIZE,
    sizes: CALENDAR_CARD_SIZES,
    onSizeChange: (size) => {
      calendarPanel.current?.onCardSizeChange(size);
    },
  });

  return (
    <CardSlot
      className="dbg-studio-overall-details-calendar"
      title={(
        <div className='dbg-studio-overall-details-calendar-title'>
          Calendar
          {!!updateTimeFormatted && <span className='dbg-studio-overall-details-calendar-subtitle'>Data Time: {updateTimeFormatted}</span>}
        </div>
      )}
    >
      <div className="top-info">
        <Filter
          loading={filterLoading}
          dateRange={dateRange}
          onChange={handleFilterChange}
        />
      </div>
      <FullScreenScale
        title="Calendar"
        smallerBtnDisabled={calendarCardSize === MIN_CALENDAR_CARD_SIZE}
        biggerBtnDisabled={calendarCardSize === MAX_CALENDAR_CARD_SIZE}
        onSmaller={handleCalendarCardSmallerSize}
        onBigger={handleCalendarCardBiggerSize}
        onFullScreenChange={() => {
          resetCalendarCardSize();
        }}
      >
        {({ fullScreenContent }) => {
          return (
            <CalendarPanel
              ref={calendarPanel}
              isStudioCalendar
              isLoading={loading || filterLoading}
              isNoPermission={isNoPermission}
              calendarCardSize={calendarCardSize}
              fullScreenContent={fullScreenContent}
              fixedBottomScrollbarStyle={{ bottom: 10 }}
              legendSelected={legendSelected}
              dateType={dateType}
              data={legendList || []}
              getPageContainerEl={() => document.querySelector('.tea-layout__content.dbg-game-details')}
              onCardClick={handleCardClick}
              topBarRightElement={
                <>
                  <LegendSign
                    loading={loading || filterLoading}
                    onChange={handleLegendChange}
                    options={LENGEND_SIGN_OPT}
                  />
                </>
              }
            />
          );
        }}
      </FullScreenScale>
    </CardSlot>
  );
}

CalendarDetails.defaultProps = DEFAULT_PROPS;

export default memo(CalendarDetails);
